import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { markPickerClose, markPickerOpen } from "./pickerGuard";
import { useSignal } from "@preact/signals-react";
import dayjs from "dayjs";
import { INGDatePickerProps } from "../../library/NGFieldExtensions";
import { publishUserAction } from "../../library/analytics";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { getClassName, getTestId, getsxObject, partitionMUIClasses } from "../../library/utils";

export default function NGDatePicker({ config, context }: INGDatePickerProps) {
  const local = setupLocalState(
    config,
    {
      Disabled: useSignal(config.Disabled ?? false),
      Style: useSignal(config.Style ?? {}),
      Classes: useSignal(config.Classes ?? []),
      Value: useSignal(config.Value ?? null),
      MinDate: useSignal(config.MinDate ?? null),
      MaxDate: useSignal(config.MaxDate ?? null)
    },
    context
  );

  const handlers = setupHandlers(config, context);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        data-testid={getTestId(config)}
        data-type={config.__typename}
        className={getClassName(local.Classes)}
        sx={getsxObject(local.Style.value, {
          "& .MuiOutlinedInput-input": {
            fontSize: 12,
          },
        })}
        disabled={local.Disabled.value}
        value={local.Value.value ? dayjs(local.Value.value) : null}
        label={config.Label}
        slotProps={{
          field: {
            clearable: config.DisableClearable === true ? false : true,
            onClear: () => (local.Value.value = null),
            "data-testid": getTestId(config),
            "data-type": config.__typename,
          },
          inputAdornment: {
            sx: {
              margin: 0,
            },
          },
        }}
        onOpen={() => markPickerOpen()}
        onClose={() => markPickerClose()}
        onAccept={() => markPickerClose()}
        minDate={local.MinDate?.value && dayjs(local.MinDate.value)}
        maxDate={local.MaxDate?.value && dayjs(local.MaxDate.value)}
        {...handlers}
        onChange={(newValue) => {
          local.Value.value = newValue ? dayjs(newValue).format("YYYY-MM-DD") : null;

          const [_, classes] = partitionMUIClasses(local.Classes.value);
          const action = publishUserAction(config, context, "date_picker_change", null, {
            ElementText: config.Label,
            Value: local.Value.value,
            EventCategory: "engagement",
            ElementClass: classes.join(" "),
          });
          if (handlers["onChange"]) handlers["onChange"]("change", local.Value.value, action);
        }}
      />
    </LocalizationProvider>
  );
}
